import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { listPostsByAuthor } from "@/lib/site.functions";
import { getAuthorBySlug, ANDERS_ID, ANGELA_ID, SITE_URL } from "@/lib/authors";
import { NotFoundPage } from "@/components/not-found";

const postsQuery = (name: string) => ({
  queryKey: ["author-posts", name],
  queryFn: () => listPostsByAuthor({ data: { name } }),
});

export const Route = createFileRoute("/blog/author/$slug")({
  loader: async ({ params, context }) => {
    const author = getAuthorBySlug(params.slug);
    if (!author) throw notFound();
    await context.queryClient.ensureQueryData(postsQuery(author.name));
    return { author };
  },
  head: ({ params, loaderData }) => {
    const url = `${SITE_URL}/blog/author/${params.slug}`;
    const a = loaderData?.author;
    if (!a) return { meta: [{ title: "Author — SaaSed" }] };
    const title = `${a.name}, ${a.role} — SaaSed`;
    const isAnders = a.slug === "anders-gustafsson";
    const isAngela = a.slug === "angela-lai";
    const person = {
      "@context": "https://schema.org",
      "@type": "Person",
      ...(isAnders ? { "@id": ANDERS_ID } : isAngela ? { "@id": ANGELA_ID } : {}),
      name: a.name,
      jobTitle: isAnders ? "Co-Founder & Salesforce Expert" : a.role,
      description: a.bio,
      url,
      image: `${SITE_URL}${a.photo}`,
      sameAs: [a.linkedin],
      worksFor:
        isAnders || isAngela
          ? { "@id": "https://saased.co/#organization" }
          : { "@type": "Organization", name: "SaaSed", url: SITE_URL },
    };
    return {
      meta: [
        { title },
        { name: "description", content: a.bio },
        { property: "og:title", content: title },
        { property: "og:description", content: a.bio },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(person) }],
    };
  },
  notFoundComponent: NotFoundPage,
  component: AuthorPage,
});

function AuthorPage() {
  const { author } = Route.useLoaderData();
  const { data } = useSuspenseQuery(postsQuery(author.name));

  return (
    <section className="container-x max-w-4xl py-20 md:py-28">
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} /> All insights
      </Link>

      <div className="mt-10 flex flex-col gap-6 sm:flex-row sm:items-center">
        <img
          src={author.photo}
          alt={author.name}
          width={160}
          height={160}
          className="aspect-square h-40 w-40 shrink-0 rounded-2xl object-cover object-top"
        />
        <div>
          <h1 className="font-serif text-4xl md:text-5xl">{author.name}</h1>
          <p className="mt-2 text-muted-foreground">{author.role}</p>
          <p className="mt-4 max-w-2xl leading-relaxed">{author.bio}</p>
          <a
            href={author.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-block font-sans text-sm font-medium uppercase tracking-[0.15em] underline-offset-4 hover:underline"
          >
            LinkedIn
          </a>
        </div>
      </div>

      <h2 className="pt-16 font-serif text-2xl md:text-3xl">Articles</h2>
      {data.posts.length === 0 ? (
        <p className="mt-4 text-muted-foreground">No articles yet.</p>
      ) : (
        <ul className="mt-6 divide-y divide-border border-y border-border">
          {data.posts.map((p) => (
            <li key={p.slug}>
              <Link to="/blog/$slug" params={{ slug: p.slug }} className="group flex items-start justify-between gap-6 py-6">
                <div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(p.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}
                  </span>
                  <h3 className="mt-1 font-sans text-xl font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
                </div>
                <ArrowRight size={16} className="mt-6 shrink-0 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
